import * as React from 'react';
import Text from '../../shared/Text';
import FormValidationMessage from './form-validation-message';
import { sendEmptyTextIfNull } from '../utils/input-handlers';
import { useSetResetPasswordData } from './providers/resetPassword/hooks';
import { Box, Button, TextField, Typography } from '@mui/material';
import { FormFieldWrapper, FormSubmitWrapper, FormWrapper } from './shared-form-components';
import { AsyncButton } from '@c42/component-library';
import { AsyncState, isError, isLoading } from '@c42/constants';
import CoreClient from '@c42/core-client';
import { useHistory } from 'react-router';
import { LOGIN_INDEX } from '../constants/routes';

const ForgotPassword: React.FC = () => {
  const [username, setUsername] = React.useState(null);
  const [submitState, setSubmitState] = React.useState(AsyncState.NOT_STARTED);

  const history = useHistory();
  const setResetPasswordData = useSetResetPasswordData();

  const isUsernameEmpty = !username;
  const isUsernameInvalid = username != null && isUsernameEmpty;
  const isFormValid = !isUsernameEmpty;

  let usernameErrorText;
  if (isUsernameInvalid && isUsernameEmpty) {
    usernameErrorText = Text.get('_validate_username_or_email');
  }

  const redirectToLoginPage = () => {
    history.push(LOGIN_INDEX);
  };

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitState(AsyncState.LOADING);
    CoreClient.UserPasswordResetRequest.create({ params: { username }, useCache: false }).then(
      () => {
        setResetPasswordData((current) => ({
          ...current,
          notification: Text.get('_flash_password_reset_check_email'),
        }));
        redirectToLoginPage();
      },
      () => {
        setSubmitState(AsyncState.ERROR);
      },
    );
  };

  const changeUsernameHandler: React.FormEventHandler<any> = (event) => {
    event.persist?.();
    setUsername((event.target as HTMLInputElement).value);
  };

  return (
    <FormWrapper>
      <form onSubmit={submitHandler} name="forgotPasswordFrm" noValidate data-test="forgot-password">
        <FormValidationMessage
          message={isError(submitState) ? Text.get('login_password_reset_error') : undefined}
        />

        <Box mt={4} textAlign="center">
          <Typography variant="h5">{Text.get('login_reset_password')}</Typography>
        </Box>

        <FormFieldWrapper>
          <TextField
            autoFocus
            autoComplete="off"
            disabled={isLoading(submitState)}
            error={Boolean(usernameErrorText)}
            fullWidth
            helperText={usernameErrorText}
            inputProps={{ 'data-test': 'forgot-password-username' }}
            label={Text.get('_placeholder_username_or_email')}
            onBlur={sendEmptyTextIfNull.bind(null, changeUsernameHandler, username)}
            onChange={changeUsernameHandler}
            value={username || ''}
          />
        </FormFieldWrapper>

        <FormSubmitWrapper>
          <AsyncButton
            data-test="forgot-password-submit"
            disabled={isLoading(submitState) || !isFormValid}
            isLoading={isLoading(submitState)}
            type="submit"
            variant="contained"
          >
            {Text.get('login_reset_password')}
          </AsyncButton>
        </FormSubmitWrapper>

        <FormFieldWrapper>
          <Button
            data-test="forgot-password-back-to-signin"
            onClick={redirectToLoginPage}
            size="small"
            variant="text"
          >
            {Text.get('login_back_to_sign_in')}
          </Button>
        </FormFieldWrapper>
      </form>
    </FormWrapper>
  );
};

export default ForgotPassword;
